import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { buildSectionSelector, normalizeSectionId, sectionRenderer } from '@theme/section-renderer';

/**
 * @typedef {object} QuickOrderDrawerRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} heading
 * @property {HTMLElement} content
 * @property {HTMLElement} loading
 * @property {HTMLElement} error
 */

/**
 * Drawer that injects the quick-order-list section via Section Rendering API.
 *
 * @extends {DialogComponent}
 */
export class QuickOrderDrawerComponent extends DialogComponent {
  requiredRefs = ['dialog', 'heading', 'content', 'loading', 'error'];

  /** @type {Map<string, string>} */
  #sectionIdByPathname = new Map();

  /** @type {AbortController | null} */
  #abortController = null;

  /**
   * @param {string} productPageUrl
   * @param {string} [productTitle]
   */
  open = async (productPageUrl, productTitle = '') => {
    if (!productPageUrl) return;

    const heading = /** @type {HTMLElement | undefined} */ (this.refs.heading);
    if (productTitle && heading) {
      heading.textContent = productTitle;
    }

    this.#setLoading(true);

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    try {
      const productUrl = new URL(productPageUrl, window.location.origin);
      let sectionId = this.#sectionIdByPathname.get(productUrl.pathname);

      if (!sectionId) {
        const doc = await this.#fetchProductDocument(productUrl, signal);
        if (!doc) return;

        const sectionElement = doc.querySelector('quick-order-list-component')?.closest('.shopify-section');
        if (!sectionElement?.id) {
          this.#showError();
          this.showDialog();
          return;
        }

        sectionId = normalizeSectionId(sectionElement.id);
        this.#sectionIdByPathname.set(productUrl.pathname, sectionId);
      }

      const sectionHTML = await sectionRenderer.getSectionHTML(sectionId, !Shopify.designMode, productUrl);
      if (signal.aborted) return;

      const fragment = new DOMParser().parseFromString(sectionHTML, 'text/html');
      const sectionElement = fragment.getElementById(buildSectionSelector(sectionId));

      const content = /** @type {HTMLElement | undefined} */ (this.refs.content);
      if (!sectionElement || !content) {
        this.#showError();
        this.showDialog();
        return;
      }

      content.replaceChildren(document.importNode(sectionElement, true));
      this.#setLoading(false);
      this.showDialog();
    } catch (error) {
      if (/** @type {Error} */ (error).name === 'AbortError') return;
      this.#showError();
      this.showDialog();
      throw error;
    } finally {
      if (!signal.aborted) {
        this.#abortController = null;
      }
    }
  };

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.#abortController?.abort();
  }

  #handleDialogClose = () => {
    this.#abortController?.abort();
    this.#abortController = null;
  };

  /**
   * @param {URL} productUrl
   * @param {AbortSignal} signal
   * @returns {Promise<Document | null>}
   */
  async #fetchProductDocument(productUrl, signal) {
    const response = await fetch(productUrl.toString(), { signal });

    if (!response.ok) {
      throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
    }

    const responseText = await response.text();
    return new DOMParser().parseFromString(responseText, 'text/html');
  }

  /** @param {boolean} isLoading */
  #setLoading(isLoading) {
    const loading = /** @type {HTMLElement | undefined} */ (this.refs.loading);
    const error = /** @type {HTMLElement | undefined} */ (this.refs.error);
    const content = /** @type {HTMLElement | undefined} */ (this.refs.content);

    loading?.classList.toggle('hidden', !isLoading);
    error?.classList.add('hidden');
    if (isLoading) {
      content?.replaceChildren();
    }
  }

  #showError() {
    const loading = /** @type {HTMLElement | undefined} */ (this.refs.loading);
    const error = /** @type {HTMLElement | undefined} */ (this.refs.error);
    const content = /** @type {HTMLElement | undefined} */ (this.refs.content);

    loading?.classList.add('hidden');
    error?.classList.remove('hidden');
    content?.replaceChildren();
  }
}

if (!customElements.get('quick-order-drawer-component')) {
  customElements.define('quick-order-drawer-component', QuickOrderDrawerComponent);
}
